import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Spinner,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ModalEnviarBoletoWhatsApp, {
  useEnviarBoletoWhatsApp,
} from "./EnviarBoletoWhatsApp";
import { FaWhatsapp } from "react-icons/fa";
import { FiCopy, FiExternalLink, FiLink } from "react-icons/fi";
import { linkDoBoleto } from "../../../../constants/linkPagamento";
import { useSetRecoilState, useRecoilState } from "recoil";
import { refreshGlobal } from "../../../../atoms/refreshGlobal";
import TitleFlex from "../../vendasDigitadas/defaultComponents/titleFlex";
import {
  CNPJFormater,
  CPFFormater,
} from "../../../../defaultFunctions/formaters";
import { dateForList } from "../../../../constants/dataFormat";
import copy from "copy-to-clipboard";
import { customColors } from "../../../../atoms/customColors";
import { toast } from "react-toastify";
import VendasBoletoIndividualApi from "../../../../services/VendasBoletoIndividuaApi";

interface ModalRevisãoVendaBoletoProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  objeto: any;
  response?: any;
  setResponseToModal: (response: any) => void;
  setBoleto: (boleto: any) => void;
  initialValues: any;
  setLoadingState: (loading: boolean) => void;
}

// CORREÇÃO: Funções para formatar corretamente os juros e multa
const getInterestDisplayText = (regrasJuros: any) => {
  if (!regrasJuros || !regrasJuros.mode || regrasJuros.mode === "SEM_JUROS") {
    return "Sem Juros";
  }

  const value = parseFloat(regrasJuros.value) / 100; // Converter de centavos para valor real

  if (regrasJuros.mode === "FIXED") {
    if (regrasJuros.frequency === "DAILY") {
      return `Valor Diário: R$ ${value.toFixed(2)}`;
    }
  } else if (regrasJuros.mode === "PERCENTAGE") {
    if (regrasJuros.frequency === "DAILY") {
      return `Percentual Diário: ${value.toFixed(3)}%`;
    } else if (regrasJuros.frequency === "MONTHLY") {
      return `Percentual Mensal: ${value.toFixed(2)}%`;
    } else if (regrasJuros.frequency === "ANNUALLY") {
      return `Percentual Anual: ${value.toFixed(2)}%`;
    }
  }

  return "Sem Juros";
};

const getMultaDisplayText = (regrasMulta: any) => {
  if (!regrasMulta || !regrasMulta.mode || regrasMulta.mode === "SEM_MULTA") {
    return "Sem Multa";
  }

  const value = parseFloat(regrasMulta.value) / 100;

  if (regrasMulta.mode === "FIXED") {
    return `Valor Fixo: R$ ${value.toFixed(2)}`;
  } else if (regrasMulta.mode === "PERCENTAGE") {
    return `Percentual: ${value.toFixed(2)}%`;
  }

  return "Sem Multa";
};

const getDescontoDisplayText = (regrasDesconto: any) => {
  if (
    !regrasDesconto ||
    !regrasDesconto.mode ||
    regrasDesconto.mode === "SEM_DESCONTO"
  ) {
    return "Sem Desconto";
  }

  const value = parseFloat(regrasDesconto.value) / 100;
  const limite = regrasDesconto.limit_at
    ? ` (até ${moment(regrasDesconto.limit_at).format("DD/MM/YYYY")})`
    : "";

  if (regrasDesconto.mode === "FIXED") {
    return `Valor Fixo: R$ ${value.toFixed(2)}${limite}`;
  } else if (regrasDesconto.mode === "PERCENTAGE") {
    return `Percentual: ${value.toFixed(2)}%${limite}`;
  }

  return "Sem Desconto";
};

export default function ModalRevisãoVendaBoleto({
  isOpen,
  setIsOpen,
  objeto,
  response,
  setResponseToModal,
  setBoleto,
  initialValues,
  setLoadingState,
}: ModalRevisãoVendaBoletoProps) {
  const finalRef = React.useRef(null);
  const navigate = useNavigate();
  const setRefreshGlobal = useSetRecoilState(refreshGlobal);
  const refresh = useRecoilState(refreshGlobal)[0];
  const colors = useRecoilState(customColors)[0];
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [localLoadingState, setLocalLoadingState] = useState(false);
  // Envio manual por WhatsApp — abre a conversa pronta, não integra nada.
  const whatsapp = useEnviarBoletoWhatsApp();

  const onClose = () => {
    setIsOpen(false);
    setIsConfirmed(false);
    setResponseToModal(null);
    setLoadingState(false);
  };

  const handleConfirm = async () => {
    setLocalLoadingState(true);
    setLoadingState(true);
    try {
      const response = await VendasBoletoIndividualApi.postVendaBolePix(objeto);
      setResponseToModal(response);
      setBoleto(initialValues);
      setIsConfirmed(true);
      toast.success("Boleto criado com sucesso!");
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data._error) {
        const { message, reasons } = error.response.data._error.error;
        const errorTranslations: { [key: string]: string } = {
          "Late fee mode is not valid": "O modo de multa está inválido",
          "Late fee start date must be greater than the expiration date":
            "A data de início da multa deve ser maior que a data de vencimento",
          "Interest mode is not valid": "O modo de juros está inválido",
          "Invalid value fields": "Campos de valor inválidos",
          "Amount is more than limit": "O valor excede o limite permitido",
        };
        const translatedReasons = reasons.map(
          (reason: string) => errorTranslations[reason] || reason
        );
        toast.error(
          `Erro ao gerar o boleto: ${message}. Motivos: ${translatedReasons.join(
            "; "
          )}`
        );
      } else {
        const errorMessage =
          error.response?.data?.message || JSON.stringify(error);
        toast.error(errorMessage);
      }
    } finally {
      setLocalLoadingState(false);
      setLoadingState(false);
    }
  };

  return (
    <Modal
      finalFocusRef={finalRef}
      isOpen={isOpen}
      onClose={onClose}
      size={"2xl"}
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <Flex w={"100%"} justifyContent={"center"}>
            <Text fontWeight={"bold"} color={colors.GREENMONEDD}>
              {isConfirmed
                ? "Venda gerada com sucesso!"
                : "Revisar Dados do Boleto"}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          {objeto && (
            <>
              <TitleFlex item={"Dados do Boleto"} />
              <Flex flexDir={"column"} px={4}>
                <Text>
                  Valor de Cobrança:{" "}
                  {objeto.valor
                    ? objeto.valor.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })
                    : "R$ 0,00"}
                </Text>
                <Text>
                  Data de Vencimento:{" "}
                  {moment(objeto.dataVencimento).format(dateForList)}
                </Text>
                {isConfirmed && response && (
                  <>
                    <Text fontSize={"16px"} fontWeight={"bold"}>
                      {response.barcode}
                    </Text>
                    <Text fontSize={"12px"}>Código de Barras</Text>
                    <Text>Id da Transação: {response.id}</Text>
                    {/* EMV (Pix Copia e Cola) removido — decisão de produto de
                        não seguir com o QR code PIX. O campo nem é mais gravado
                        no M2 (a core salva Emv = ""), então exibi-lo aqui só
                        mostraria vazio. */}
                  </>
                )}
              </Flex>
              <TitleFlex item={"Dados do Pagador"} />
              <Flex flexDir={"column"} px={4}>
                <Text>Nome do Pagador: {objeto.nomePagador}</Text>
                <Text>
                  Documento do Pagador:{" "}
                  {objeto.cpfPagador && objeto.cpfPagador.length === 11
                    ? CPFFormater(objeto.cpfPagador)
                    : CNPJFormater(objeto.cpfPagador)}
                </Text>
                <Text>Email: {objeto.emailPagador}</Text>
              </Flex>
              <TitleFlex item={"Endereço de Cobrança"} />
              <Flex flexDir={"column"} px={4}>
                <Text fontSize={"13px"}>
                  {`${objeto.enderRua}, ${objeto.enderNumero}/${objeto.enderBairro}-${objeto.enderCidade}-${objeto.enderEstado}`}
                </Text>
                <Text>CEP: {objeto.enderCEP}</Text>
              </Flex>
              {objeto && objeto.permitirRegras && (
                <>
                  <TitleFlex item={"Regras de Juros, Multa e Desconto"} />
                  <Flex flexDir={"column"} px={4}>
                    {/* CORREÇÃO: Usar as novas funções de formatação */}
                    <Text>
                      Juros: {getInterestDisplayText(objeto.RegrasJuros)}
                    </Text>
                    <Text>
                      Multa: {getMultaDisplayText(objeto.RegrasMulta)}
                    </Text>
                    <Text>
                      Desconto: {getDescontoDisplayText(objeto.RegrasDesconto)}
                    </Text>
                  </Flex>
                </>
              )}
            </>
          )}

          {/*
            Depois de gerar, o operador precisa DECIDIR o que fazer — e cada
            caminho é um botão explícito, com rótulo dizendo o que copia.
            Antes havia só "Copiar Código de Barras" (que copiava o barcode,
            não a linha digitável), o WhatsApp e um "Abrir Boleto" espremidos
            no rodapé, sem o link da página do boleto em lugar nenhum.
          */}
          {isConfirmed && response && (
            <Box mt={5} p={4} borderWidth="1px" borderRadius="md">
              <Text fontWeight="bold" fontSize="14px" mb={1}>
                Boleto gerado. O que você quer fazer agora?
              </Text>
              <Text fontSize="12px" color="gray.600" mb={3}>
                Nada é enviado automaticamente ao pagador neste momento — os
                avisos de vencimento seguem a régua configurada.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Button
                  leftIcon={<FiExternalLink />}
                  bgColor={colors.VARIANT}
                  color={colors.WHITE}
                  onClick={() =>
                    window.open(
                      linkDoBoleto(response.id),
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Abrir boleto
                </Button>

                <Button
                  leftIcon={<FiLink />}
                  variant="outline"
                  onClick={() => {
                    copy(linkDoBoleto(response.id));
                    toast.success("Link do boleto copiado.");
                  }}
                >
                  Copiar link do boleto
                </Button>

                <Button
                  leftIcon={<FiCopy />}
                  variant="outline"
                  // linhaDigitavel é o que a pessoa digita no banco; o barcode
                  // é o código bruto. Só cai nele se a linha não vier.
                  isDisabled={!response.linhaDigitavel && !response.barcode}
                  onClick={() => {
                    copy(response.linhaDigitavel || response.barcode || "");
                    toast.success(
                      response.linhaDigitavel
                        ? "Linha digitável copiada."
                        : "Código de barras copiado (linha digitável não veio)."
                    );
                  }}
                >
                  Copiar linha digitável
                </Button>

                <Button
                  leftIcon={<FaWhatsapp />}
                  colorScheme="whatsapp"
                  onClick={() =>
                    whatsapp.abrir({
                      id: response.id,
                      nomePagador: objeto?.nomePagador,
                      numeroPagador: objeto?.numeroPagador,
                      telefone: objeto?.telefone,
                      valor: objeto?.valor,
                      dataVencimento: objeto?.dataVencimento,
                      linhaDigitavel:
                        response.linhaDigitavel || response.barcode,
                      numeroDocumento: response.numeroDocumento,
                    })
                  }
                >
                  Enviar para WhatsApp
                </Button>
              </SimpleGrid>
            </Box>
          )}
        </ModalBody>
        <ModalFooter justifyContent={"space-evenly"}>
          {!isConfirmed ? (
            <>
              <Button
                background={colors.GRAY}
                color={"white"}
                mr={3}
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                isDisabled={localLoadingState}
                onClick={handleConfirm}
              >
                {localLoadingState ? <Spinner size="sm" mr={2} /> : null}
                Confirmar Criação
              </Button>
            </>
          ) : (
            // As ações do boleto ficam no corpo, não aqui: são quatro e cada
            // uma precisa de rótulo legível. No rodapé sobra só o encerramento.
            <Button
              background={colors.GRAY}
              color={"white"}
              onClick={onClose}
            >
              Fechar
            </Button>
          )}
        </ModalFooter>
      </ModalContent>

      <ModalEnviarBoletoWhatsApp
        boleto={whatsapp.boleto}
        isOpen={whatsapp.isOpen}
        onClose={whatsapp.onClose}
      />
    </Modal>
  );
}
